import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, KeyRound, Lock, LockKeyhole, LogOut, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { logout } from "@/lib/api/auth";
import { lockSession } from "@/lib/auth/session-lock";
import { getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";
import { getProfiles, removeProfile, subscribeProfiles } from "@/lib/stores/profile-roster-store";
import { getSelfAvailability, setSelfAvailability, subscribePresence } from "@/lib/realtime/presence-service";
import PresenceDot, { AVAILABILITY_OPTIONS, getPresenceVisual } from "@/components/presence/presence-dot";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { CredentialsDialog } from "@/components/auth/credentials-dialog";
import { ChangePasswordDialog } from "@/components/auth/change-password-dialog";
import { PinEntryDialog } from "@/components/auth/pin-entry-dialog";
import { PinSetupDialog, maybePromptPinSetupAfterLogin } from "@/components/auth/pin-setup-dialog";
import { cn, MOD_KEY_LABEL } from "@/lib/utils";

function initialsFor(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("");
}

function formatRelativeTime(iso) {
  const ts = Date.parse(String(iso || ""));
  if (!Number.isFinite(ts)) return "";
  const minutes = Math.floor((Date.now() - ts) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(ts).toLocaleDateString();
}

// A roster tile. NOT a <button>: the remove ✕ inside is a real button, and buttons must never
// nest — the tile is a keyboard-operable div instead.
function ProfileTile({ profile, isActive, onSelect, onRemove }) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(profile)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onSelect(profile);
        }
      }}
      className="group flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
    >
      <Avatar className="h-8 w-8 shrink-0">
        <AvatarFallback className="text-xs font-semibold">
          {initialsFor(profile.displayName)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm font-medium text-foreground">{profile.displayName}</span>
          {profile.hasPin ? (
            <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[10px] text-muted-foreground">
              PIN
            </Badge>
          ) : null}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {isActive ? "Current profile" : formatRelativeTime(profile.lastUsedAt) || profile.loginId}
        </p>
      </div>
      {isActive ? (
        <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden />
      ) : (
        <button
          type="button"
          className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
          title="Remove from this device"
          aria-label={`Remove ${profile.displayName} from this device`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(profile);
          }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * Sidebar-footer current-user chip + popover with the device's profile roster: switch (PIN or
 * password), add an account, set/change the current user's PIN, log out. Selecting a dormant
 * profile re-authenticates server-side — nothing but descriptors lives on the device.
 */
function ProfileSwitcher({ collapsed = false }) {
  const [authUser, setAuthUserState] = useState(() => getAuthUser());
  const [profiles, setProfilesState] = useState(() => getProfiles());
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [pinEntry, setPinEntry] = useState({ open: false, profile: null });
  const [credentials, setCredentials] = useState({ open: false, loginId: null });
  const [pinSetupOpen, setPinSetupOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  // Own availability status (Available/Busy/…): shown as a dot on the chip avatar and
  // settable from the popover. Mirrors presence-service (optimistic + server echo).
  const [selfStatus, setSelfStatus] = useState(() => getSelfAvailability());

  useEffect(() => subscribeAuthStateChange(() => setAuthUserState(getAuthUser())), []);
  useEffect(() => subscribeProfiles(setProfilesState), []);
  useEffect(() => subscribePresence(() => setSelfStatus(getSelfAvailability())), []);

  const handleSelectStatus = (value) => {
    setPopoverOpen(false);
    setSelfAvailability(value).catch(() => toast.error("Could not update your status."));
  };

  if (!authUser) return null;

  const activeUserId = String(authUser.id || "");
  const displayName = String(authUser.name || authUser.displayName || authUser.loginId || "").trim();

  const handleSelect = (profile) => {
    setPopoverOpen(false);
    if (profile.userId === activeUserId) return;
    if (profile.hasPin) {
      setPinEntry({ open: true, profile });
    } else {
      setCredentials({ open: true, loginId: profile.loginId });
    }
  };

  // Workspace destruction + session clear live in logout() itself; the auth event handles
  // the rest (freeze, lock screen) — nothing to do here beyond closing the popover.
  const handleLogout = async () => {
    setPopoverOpen(false);
    try {
      await logout();
    } catch (error) {
      console.error("[Auth] logout request failed:", error);
    }
  };

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted",
              collapsed && "justify-center px-1",
            )}
            title={collapsed ? displayName : undefined}
            aria-label={`Signed in as ${displayName}. Open profile switcher.`}
          >
            <span className="relative shrink-0">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs font-semibold">{initialsFor(displayName)}</AvatarFallback>
              </Avatar>
              {authUser.isEmployee ? (
                <PresenceDot online availability={selfStatus} className="absolute -bottom-0.5 -right-0.5" />
              ) : null}
            </span>
            {!collapsed && (
              <>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
                  <p className="truncate text-xs text-muted-foreground">{authUser.loginId || ""}</p>
                </div>
                <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
              </>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start" side="top">
          <p className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Profiles on this device
          </p>
          <ScrollArea viewportClassName="max-h-64">
            <div className="space-y-0.5">
              {profiles.map((profile) => (
                <ProfileTile
                  key={profile.userId}
                  profile={profile}
                  isActive={profile.userId === activeUserId}
                  onSelect={handleSelect}
                  onRemove={(p) => removeProfile(p.userId)}
                />
              ))}
            </div>
          </ScrollArea>
          {authUser.isEmployee ? (
            <>
              <Separator className="my-1.5" />
              <p className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Status
              </p>
              <div className="space-y-0.5">
                {AVAILABILITY_OPTIONS.map((option) => {
                  const visual = getPresenceVisual(option.value);
                  const isActive = selfStatus === option.value;
                  return (
                    <Button
                      key={option.value}
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start gap-2.5 px-2 font-normal"
                      onClick={() => handleSelectStatus(option.value)}
                      title={option.value === "dnd" ? "Also mutes notifications" : undefined}
                    >
                      <span
                        className={cn(
                          "inline-flex h-2.5 w-2.5 shrink-0 items-center justify-center rounded-full",
                          visual.dotClassName,
                        )}
                        aria-hidden
                      >
                        {visual.showDndBar ? <span className="h-[2px] w-[5px] rounded-full bg-white" /> : null}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-left">{option.label}</span>
                      {isActive ? <Check className="h-4 w-4 shrink-0 text-primary" aria-hidden /> : null}
                    </Button>
                  );
                })}
              </div>
            </>
          ) : null}
          <Separator className="my-1.5" />
          <div className="space-y-0.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2.5 px-2 font-normal"
              onClick={() => {
                setPopoverOpen(false);
                // Empty string (not null): the login form must start blank — null would fall
                // back to the remembered login id, silently prefilling the CURRENT user's
                // email into a dialog meant for a colleague's account.
                setCredentials({ open: true, loginId: "" });
              }}
            >
              <Plus className="h-4 w-4 text-muted-foreground" />
              Add account
            </Button>
            {authUser.isEmployee ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full justify-start gap-2.5 px-2 font-normal"
                onClick={() => {
                  setPopoverOpen(false);
                  setPinSetupOpen(true);
                }}
              >
                <KeyRound className="h-4 w-4 text-muted-foreground" />
                {authUser.hasPin ? "Change PIN" : "Set PIN"}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2.5 px-2 font-normal"
              onClick={() => {
                setPopoverOpen(false);
                setChangePasswordOpen(true);
              }}
            >
              <LockKeyhole className="h-4 w-4 text-muted-foreground" />
              Change password
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2.5 px-2 font-normal"
              onClick={() => {
                setPopoverOpen(false);
                // Lock ≠ logout: only clears the session — workspaces freeze (nothing
                // destroyed) and PIN re-entry thaws the same tree.
                lockSession();
              }}
            >
              <Lock className="h-4 w-4 text-muted-foreground" />
              Lock
              <span className="ml-auto text-xs text-muted-foreground">{`${MOD_KEY_LABEL}L`}</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-2.5 px-2 font-normal"
              onClick={() => void handleLogout()}
            >
              <LogOut className="h-4 w-4 text-muted-foreground" />
              Log out
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <PinEntryDialog
        open={pinEntry.open}
        onOpenChange={(open) => setPinEntry((prev) => ({ ...prev, open }))}
        profile={pinEntry.profile}
        onUsePassword={(profile) => setCredentials({ open: true, loginId: profile?.loginId || "" })}
      />

      <CredentialsDialog
        open={credentials.open}
        onOpenChange={(open) => setCredentials((prev) => ({ ...prev, open }))}
        prefilledLoginId={credentials.loginId ?? undefined}
        onSuccess={(result, { password } = {}) => {
          maybePromptPinSetupAfterLogin(result, password);
        }}
      />

      <PinSetupDialog open={pinSetupOpen} onOpenChange={setPinSetupOpen} />
      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
    </>
  );
}

export default ProfileSwitcher;
